import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const sectionClass = 'border-t border-white/10 pt-8 md:pt-10';
const headingClass = 'text-xl font-bold tracking-tight text-white md:text-2xl';
const bodyClass = 'mt-4 space-y-4 text-sm leading-7 text-gray-400 md:text-base md:leading-8';

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>About Us | PhantomPathVPN</title>
        <meta name="description" content="Learn how PhantomPathVPN provides pay as you go VPN access, travel eSIM data and temporary UK and USA virtual numbers without mandatory subscriptions." />
        <link rel="canonical" href="https://phantompathvpn.com/about-us" />
      </Helmet>

      <main className="min-h-screen bg-[#0a0a0a] px-4 pb-24 pt-32 text-gray-300">
        <div className="container mx-auto max-w-5xl">
          <header className="border-b border-white/10 pb-10 md:pb-12">
            <p className="text-center text-xs font-bold uppercase tracking-[0.18em] text-[#dbe4ee]">PhantomPathVPN Ltd</p>
            <h1 className="mt-3 text-center text-3xl font-black tracking-[-0.025em] text-[#f4f8ff] md:text-5xl">
              About PhantomPath
            </h1>
            <div className="mt-10 max-w-4xl space-y-4 text-base leading-8 text-gray-300 md:mt-12 md:text-lg">
              <p>PhantomPathVPN Ltd is a UK privacy and connectivity company built around one simple idea:</p>
              <p className="font-semibold text-white">People should be able to buy the digital access they need without being forced into another monthly subscription or permanent account.</p>
            </div>
          </header>

          <div className="mt-12 space-y-12 md:mt-16 md:space-y-16">
            <section>
              <h2 className={headingClass}>Three services through one platform</h2>
              <div className="mt-7 grid gap-8 md:grid-cols-3">
                <article className="border-t border-[#00ffc8]/35 pt-5">
                  <h3 className="text-base font-bold text-white">Pay as you go VPN access</h3>
                  <p className="mt-3 text-sm leading-7 text-gray-400">Choose one hour, 24 hours, 14 days or 30 days of VPN access without creating a PhantomPath account. Your chosen payment provider may process the information required to complete the transaction.</p>
                </article>
                <article className="border-t border-[#00ffc8]/35 pt-5">
                  <h3 className="text-base font-bold text-white">Travel eSIM data</h3>
                  <p className="mt-3 text-sm leading-7 text-gray-400">Choose your destination, data allowance and duration. Your eSIM is delivered digitally after payment, ready for activation on a compatible device.</p>
                </article>
                <article className="border-t border-[#00ffc8]/35 pt-5">
                  <h3 className="text-base font-bold text-white">UK and USA virtual numbers</h3>
                  <p className="mt-3 text-sm leading-7 text-gray-400">Temporary virtual numbers with optional calling and messaging allowances. Availability and compatibility depend on the selected number, package and upstream telecommunications provider.</p>
                </article>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={headingClass}>Built for the time you need</h2>
              <div className={bodyClass}>
                <p>Most digital services are designed to keep customers permanently subscribed.</p>
                <p>PhantomPath works differently.</p>
                <div className="grid gap-2 py-2 font-semibold text-white sm:grid-cols-2">
                  <p>Choose the service you need.</p>
                  <p>Choose how long you need it.</p>
                  <p>Pay once.</p>
                  <p>Use it without automatic renewal.</p>
                </div>
                <p>There are no hidden subscription tiers and nothing continues unless you decide to purchase again.</p>
              </div>
            </section>

            <div className="grid gap-12 md:grid-cols-2 md:gap-10">
              <section className={sectionClass}>
                <h2 className={headingClass}>How our VPN privacy works</h2>
                <div className={bodyClass}>
                  <p>PhantomPath VPN traffic is routed through high speed RAM only servers and protected in transit using WireGuard cryptography.</p>
                  <p>PhantomPath does not retain browsing activity, DNS queries or VPN traffic content.</p>
                  <p>VPN access codes are temporary and remain active only for the duration of the purchased session.</p>
                </div>
              </section>

              <section className={sectionClass}>
                <h2 className={headingClass}>Communications and data retention</h2>
                <div className={bodyClass}>
                  <p>Message payloads processed through PhantomPath controlled systems are removed server side within 30 seconds of delivery.</p>
                  <p>Message history and call audio are not retained in the PhantomPath customer database.</p>
                  <p>Virtual number services are provided through licensed upstream telecommunications providers. Compatibility with verification services depends on the policies of each platform, and SMS delivery cannot be guaranteed for every external service.</p>
                  <p>PhantomPath virtual numbers do not support emergency calling. Customers must maintain another way to contact emergency services.</p>
                </div>
              </section>
            </div>

            <section className={sectionClass}>
              <h2 className={headingClass}>Payments and personal information</h2>
              <div className={`${bodyClass} max-w-4xl`}>
                <p>Payments are processed independently by Stripe, PayPal or our UK mobile billing provider, depending on the selected payment method.</p>
                <p>PhantomPath does not store complete card numbers, security codes or payment credentials in its customer database.</p>
                <p>We retain only the limited order information required for provisioning, accounting, refunds, fraud prevention, regulatory compliance and customer support.</p>
                <p>This operational information remains separate from VPN traffic, browsing activity, session content and communications.</p>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={headingClass}>The company behind the platform</h2>
              <div className={`${bodyClass} max-w-4xl`}>
                <p>PhantomPathVPN Ltd is a registered UK company.</p>
                <dl className="grid gap-x-8 gap-y-4 border-y border-white/10 py-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">Company Number</dt>
                    <dd className="mt-1 font-semibold text-white">16958507</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">ICO Registration</dt>
                    <dd className="mt-1 font-semibold text-white">ZC089271</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">Customer Support</dt>
                    <dd className="mt-1"><a href="mailto:support@phantompathvpn.com" className="font-semibold text-white hover:text-[#00ffc8]">support@phantompathvpn.com</a></dd>
                  </div>
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">Telephone</dt>
                    <dd className="mt-1"><a href="tel:03333130127" className="font-semibold text-white hover:text-[#00ffc8]">0333 313 0127</a></dd>
                  </div>
                </dl>
                <p>PhantomPath operates with clear acceptable use, refund, complaints, privacy and vulnerable customer policies.</p>
                <p>Our services are designed for lawful privacy, security, communication and travel connectivity.</p>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={headingClass}>Founded by Zack</h2>
              <div className={`${bodyClass} max-w-4xl`}>
                <p>PhantomPath was founded by Zack after nearly ten years working with paint booths, spray guns and repairing cars.</p>
                <p>The precision learned through that work, combined with the discipline and resilience developed through Muay Thai, shaped the way PhantomPath was built.</p>
                <p>You can read the full story on the Meet the Founder page.</p>
              </div>
              <div className="mt-7 flex flex-wrap gap-x-7 gap-y-4 text-sm font-bold">
                <Link to="/zacks-manifesto" className="border-b border-[#00ffc8]/55 pb-1 text-white transition-colors hover:text-[#00ffc8]">Meet the Founder</Link>
                <Link to="/#pricing" className="border-b border-white/25 pb-1 text-gray-300 transition-colors hover:text-white">Explore Our Passes</Link>
              </div>
            </section>

            <p className="border-t border-white/10 pt-10 text-lg font-semibold leading-8 text-white md:text-xl">
              Choose what you need. Use it for the required period. Move on without another monthly commitment.
            </p>
          </div>
        </div>
      </main>
    </>
  );
};

export default AboutUs;
